import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { getSupabaseClient } from '../../lib/supabaseClient'
import { notifyLeadByEmail } from '../../lib/notifyLead'

const NewsletterSection: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: ''
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    setSubmitError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setSubmitError(null)
    try {
      const supabase = getSupabaseClient()
      const { error } = await supabase.from('leads').insert({
        source: 'newsletter',
        full_name: formData.fullName,
        email: formData.email,
        phone: null,
        payload: {},
      })
      if (error) throw error
      notifyLeadByEmail({ source: 'newsletter', full_name: formData.fullName, email: formData.email })
      setSubmitted(true)
      setFormData({ fullName: '', email: '' })
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Signup failed. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section 
      className="relative"
      style={{ 
        backgroundColor: 'rgb(246, 246, 241)', // Exact Figma background
        height: '540px'
      }}
    >
      {/* Top Divider Line */}
      <div 
        className="absolute top-0 left-0 right-0 h-px"
        style={{ backgroundColor: 'rgb(82, 76, 61)' }}
      />

      <div className="relative max-w-[1200px] mx-auto h-full flex flex-col justify-center px-4">
        
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6"
        >
          <p 
            className="text-lg font-medium tracking-wide"
            style={{ color: 'rgb(30, 29, 28)' }}
          >
            Newsletter
          </p>
        </motion.div>

        {/* Main Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="text-center mb-8"
        >
          <h2 
            className="text-4xl md:text-5xl font-normal leading-tight"
            style={{ 
              color: 'rgb(30, 29, 28)',
              fontWeight: '400'
            }}
          >
            Hudson My Home Updates
          </h2>
        </motion.div>

        {/* Description */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center mb-12 max-w-md mx-auto"
        >
          <p 
            className="text-base leading-relaxed"
            style={{ color: 'rgb(30, 29, 28)' }}
          >
            Home care insights? Absolutely. Seasonal tips? Of course. Spam? Never.
            Subscribe to receive exclusive member updates and proactive home care guidance — delivered to your inbox.
          </p>
        </motion.div>

        {/* Newsletter Form */}
        {submitted ? (
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-md mx-auto mb-8"
            style={{ color: 'rgb(30, 29, 28)', fontWeight: 500 }}
          >
            Thanks for subscribing. You&apos;ll receive our updates soon.
          </motion.p>
        ) : (
        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          onSubmit={handleSubmit}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-lg mx-auto mb-8"
        >
          {/* Full Name Input */}
          <div className="relative">
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              placeholder="Full Name"
              required
              className="w-full sm:w-[180px] h-[38px] px-3 text-base border transition-colors duration-200 focus:outline-none focus:border-opacity-80"
              style={{ 
                borderColor: 'rgb(82, 76, 61)',
                color: 'rgb(30, 29, 28)',
                backgroundColor: 'transparent'
              }}
            />
          </div>

          {/* Email Input */}
          <div className="relative">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email Address"
              required
              className="w-full sm:w-[180px] h-[38px] px-3 text-base border transition-colors duration-200 focus:outline-none focus:border-opacity-80"
              style={{ 
                borderColor: 'rgb(82, 76, 61)',
                color: 'rgb(30, 29, 28)',
                backgroundColor: 'transparent'
              }}
            />
          </div>

          {submitError && <p className="text-red-600 text-sm w-full text-center sm:text-left">{submitError}</p>}
          {/* Submit Button */}
          <motion.button
            whileHover={{ scale: submitting ? 1 : 1.05 }}
            whileTap={{ scale: 0.95 }}
            type="submit"
            disabled={submitting}
            className="w-full sm:w-[97px] h-[38px] text-white font-medium text-sm transition-all duration-200 hover:opacity-90 disabled:opacity-70"
            style={{ backgroundColor: 'rgb(82, 76, 61)' }}
          >
            {submitting ? 'Signing up…' : 'Sign up'}
          </motion.button>
        </motion.form>
        )}

        {/* Disclaimer */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.5 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center max-w-md mx-auto"
        >
          <p 
            className="text-sm leading-relaxed"
            style={{ color: 'rgba(30, 29, 28, 0.5)' }}
          >
            This form connects you to exclusive Hudson My Home insights and updates. 
            Unsubscribe at any time. Your privacy is our priority.
          </p>
        </motion.div>

        {/* Mobile Layout Optimization */}
        <div className="sm:hidden mt-8 space-y-4">
          <input
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleInputChange}
            placeholder="Full Name"
            className="w-full h-12 px-4 text-base border rounded-md"
            style={{ 
              borderColor: 'rgb(82, 76, 61)',
              color: 'rgb(30, 29, 28)'
            }}
          />
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Email Address"
            className="w-full h-12 px-4 text-base border rounded-md"
            style={{ 
              borderColor: 'rgb(82, 76, 61)',
              color: 'rgb(30, 29, 28)'
            }}
          />
          <button
            type="submit"
            className="w-full h-12 text-white font-medium rounded-md"
            style={{ backgroundColor: 'rgb(82, 76, 61)' }}
          >
            Subscribe to Hudson My Home Updates
          </button>
        </div>
      </div>

      {/* Success State (Hidden by default, can be shown with state management) */}
      <div className="hidden absolute inset-0 flex items-center justify-center">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center max-w-md mx-auto p-8 bg-white rounded-lg shadow-lg"
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Welcome to Hudson My Home!</h3>
          <p className="text-gray-600">
            Thank you for subscribing. You'll receive our proactive home management insights soon.
          </p>
        </motion.div>
      </div>
    </section>
  )
}

export default NewsletterSection 